import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { airports } from "@/data/airports";
import { airportDining, diningAirportCodes } from "@/data/airportDining";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { UtensilsCrossed, ChevronRight } from "lucide-react";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { SchemaMarkup, generateFAQSchema } from "@/components/SchemaMarkup";
import { AppStoreCTA } from "@/components/AppStoreCTA";

const DiningHub = () => {
  const url = "https://airportbuddy.app/airport-dining";

  const rows = diningAirportCodes.map((code) => {
    const info = airportDining[code];
    const airport = airports.find((a) => a.code === code);
    return { code, info, name: airport?.name ?? code, city: airport?.city.split(",")[0] ?? "" };
  });

  const faqs = [
    {
      question: "Which US airport has the best food?",
      answer:
        "Portland, Chicago O'Hare and Phoenix Sky Harbor are regularly rated highest, largely because they brought in local restaurants rather than national chains. O'Hare's Tortas Frontera, Denver's Root Down and Phoenix's Barrio Cafe are all airport outposts of well-regarded city restaurants.",
    },
    {
      question: "Should I eat before or after airport security?",
      answer:
        "Almost always after. The selection past the checkpoint is far better at most US airports, and eating first risks missing your flight if the line is long. Check the live security wait for your airport, clear the checkpoint, then eat at your gate area.",
    },
    {
      question: "What time do airport restaurants open?",
      answer:
        "Coffee counters typically open between 4:30am and 5:00am at hub airports to serve the first departure bank. Full restaurants usually open between 6:00am and 7:00am, and most close between 9:00pm and 10:00pm.",
    },
    {
      question: "Is airport food more expensive?",
      answer:
        "Usually yes, though several airports including Portland and Phoenix have 'street pricing' policies that cap what vendors can charge above their city locations. Expect to pay roughly 10-25% more than the same restaurant downtown.",
    },
  ];

  return (
    <>
      <Helmet>
        <title>Airport Restaurants & Dining Guides by Terminal | Airport Buddy</title>
        <meta
          name="description"
          content="Where to eat at major US airports. Restaurants by terminal, opening hours, what's open early, and the local places worth skipping the chains for."
        />
        <link rel="canonical" href={url} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Airport Restaurants & Dining Guides" />
        <meta
          property="og:description"
          content="Restaurants by terminal at major US airports, with hours and live security wait times."
        />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
      </Helmet>

      <SchemaMarkup schema={generateFAQSchema(faqs)} />

      <div className="min-h-screen bg-gradient-to-br from-background via-secondary/5 to-accent/10">
        <div className="container mx-auto px-4 pt-4 pb-12">
          <Breadcrumbs
            items={[
              { label: "Home", href: "/" },
              { label: "Airport dining", href: "/airport-dining" },
            ]}
          />

          <div className="max-w-3xl mx-auto space-y-6">
            <header className="space-y-3 mt-2">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground">
                Airport restaurants and dining guides
              </h1>
              <p className="text-base md:text-lg text-muted-foreground">
                What's actually worth eating at the biggest US airports — by terminal, with hours,
                early-morning options and the local restaurants hiding among the chains.
              </p>
            </header>

            <Card className="rounded-xl shadow-md">
              <CardContent className="py-6 space-y-4">
                <div className="flex items-center gap-2">
                  <UtensilsCrossed className="h-5 w-5 text-primary" aria-hidden="true" />
                  <h2 className="text-xl font-bold text-foreground">Dining guides by airport</h2>
                </div>
                <ul className="divide-y border-t">
                  {rows.map(({ code, info, name, city }) => (
                    <li key={code}>
                      <Link
                        to={`/airports/${code.toLowerCase()}/dining`}
                        className="flex items-center justify-between gap-3 py-4 min-h-[44px] active:scale-95 transition"
                      >
                        <span className="min-w-0">
                          <span className="block text-base font-semibold text-foreground">
                            {code} — {name}
                          </span>
                          <span className="block text-base text-muted-foreground">
                            {city} · {info.venueCount} places to eat · best in {info.bestTerminal}
                          </span>
                        </span>
                        <ChevronRight
                          className="h-5 w-5 text-muted-foreground shrink-0"
                          aria-hidden="true"
                        />
                      </Link>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <AppStoreCTA
              placement="dining_hub"
              headline="Eat well, still make your flight"
              subline="Airport Buddy watches your security line and tells you exactly when to leave the table. Free on iPhone."
            />

            <Card className="rounded-xl shadow-md">
              <CardContent className="py-6 space-y-4">
                <h2 className="text-xl font-bold text-foreground">Airport dining questions</h2>
                <div className="space-y-4">
                  {faqs.map((f) => (
                    <div key={f.question} className="space-y-1">
                      <h3 className="text-base font-semibold text-foreground">{f.question}</h3>
                      <p className="text-base text-muted-foreground">{f.answer}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild className="min-h-[44px] rounded-xl active:scale-95 flex-1">
                <Link to="/airports">All airport wait times</Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="min-h-[44px] rounded-xl active:scale-95 flex-1"
              >
                <Link to="/blog">Travel tips</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DiningHub;
